import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { LanguageContext } from '../../context/LanguageContext';
import { useLocation } from 'react-router-dom';

/**
 * Blog listesi sayfası için çeviri destekli SEO meta etiketlerini yöneten Helmet bileşeni
 * @param {Object} blogs - Blog listesi verisi
 * @returns {JSX.Element} - Helmet bileşeni
 */
const BlogsHelmet = ({ blogs = [], category = null }) => {
  const { currentLang } = useContext(LanguageContext);
  const location = useLocation();
  
  // Base URL
  const baseUrl = 'https://monaklinik.com';
  
  // Canonical ve hreflang URL'leri
  const canonicalUrl = `${baseUrl}${location.pathname}${location.search}`;
  
  // Türkçe ve İngilizce URL'leri
  const trUrl = `${baseUrl}/blogs${location.search}`;
  const enUrl = `${baseUrl}/en/blogs${location.search}`;
  
  // Sayfa başlığı
  let pageTitle = '';
  if (category) {
    pageTitle = currentLang === 'tr' 
      ? `${category} Kategorisi - Blog | Mona Klinik`
      : `${category} Category - Blog | Mona Clinic`;
  } else {
    pageTitle = currentLang === 'tr' 
      ? 'Blog Yazıları | Mona Klinik'
      : 'Blog Articles | Mona Clinic';
  }
  
  // Meta açıklaması
  const metaDescription = currentLang === 'tr'
    ? 'Mona Klinik diş hekimlerinin hazırladığı bilgilendirici makaleler. Diş sağlığınız için uzman önerileri ve tedavi bilgileri.'
    : 'Informative articles prepared by Mona Clinic dentists. Expert recommendations and treatment information for your dental health.';
  
  // Blog listesi için Schema.org yapısal veri işaretlemesi
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': pageTitle,
    'description': metaDescription,
    'url': canonicalUrl,
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'Mona Klinik',
      'url': baseUrl
    },
    'about': {
      '@type': 'Thing',
      'name': currentLang === 'tr' ? 'Diş Sağlığı' : 'Dental Health'
    },
    'inLanguage': currentLang === 'tr' ? 'tr-TR' : 'en-US'
  };
  
  // Eğer blog listesi varsa, blog makalelerini de ekle
  if (blogs && blogs.length > 0) {
    schemaData.mainEntity = {
      '@type': 'ItemList',
      'itemListElement': blogs.map((blog, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'url': `${baseUrl}/${currentLang === 'en' ? 'en/' : ''}blog/${blog.url || blog.slug}`
      }))
    };
  }
  
  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang etiketleri */}
      <link rel="alternate" hreflang="tr" href={trUrl} />
      <link rel="alternate" hreflang="en" href={enUrl} />
      <link rel="alternate" hreflang="x-default" href={trUrl} />
      
      {/* Sosyal medya meta etiketleri - Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={currentLang === 'tr' ? 'tr_TR' : 'en_US'} />
      <meta property="og:site_name" content="Mona Klinik" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      
      {/* Schema.org yapısal veri işaretlemesi */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};

export default BlogsHelmet;
